import { Link, NavLink, type LinkProps, type NavLinkProps } from 'react-router-dom';

import { useI18n } from './use-i18n.js';
import { usePlatformBrand } from './use-platform-brand.js';
import { localizeTo } from './locale-routing.js';

/**
 * Locale-aware `<Link>` / `<NavLink>` (SEO slice 2b — ADR-0024).
 *
 * Internal navigation must keep the visitor in their current locale: a link to
 * `/pricing` rendered while browsing `/ms/...` should go to `/ms/pricing`, not
 * drop back to English. These thin wrappers read the active locale from
 * `useI18n()` and prefix the target via `localizeTo`; the default locale (en)
 * stays on the bare path.
 *
 * Call sites import them aliased (`LocaleLink as Link`) so existing JSX is
 * unchanged — only the import line differs. `to` is a string at every call
 * site, so we narrow to `string` rather than the full router `To` union.
 */

export function LocaleLink({ to, ...rest }: Omit<LinkProps, 'to'> & { readonly to: string }) {
  const { locale } = useI18n();
  const { defaultLocale } = usePlatformBrand();
  return <Link to={localizeTo(to, locale, defaultLocale)} {...rest} />;
}

export function LocaleNavLink({ to, ...rest }: Omit<NavLinkProps, 'to'> & { readonly to: string }) {
  const { locale } = useI18n();
  const { defaultLocale } = usePlatformBrand();
  return <NavLink to={localizeTo(to, locale, defaultLocale)} {...rest} />;
}
